import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ChartType = 'bar' | 'line' | 'area' | 'table';
export type SortBy   = 'value-desc' | 'value-asc' | 'name-asc' | 'trend';

export interface CrossFilter {
  field: string;
  value: unknown;
  label: string;
  sourceChartId: string;
}

export interface DrillItem {
  label: string;
  filter: Record<string, unknown>;
}

export interface Annotation {
  id: string;
  xValue: string;
  yKey: string;
  text: string;
  createdAt: string;
}

export interface FeatureFlags {
  trendLine: boolean;
  forecast: boolean;
  benchmark: boolean;
  brush: boolean;
  comparison: boolean;
}

export interface AnnotationDraft {
  xValue: string;
  yKey: string;
}

interface DashboardState {
  crossFilter:    CrossFilter | null;
  drillState:     Record<string, DrillItem[]>;
  chartTypes:     Record<string, ChartType>;
  sortConfigs:    Record<string, SortBy>;
  topNs:          Record<string, number | null>;
  features:       Record<string, FeatureFlags>;
  pinnedKPIOrder: string[];
  annotations:    Record<string, Annotation[]>;
}

interface DashboardContextType extends DashboardState {
  setCrossFilter:    (f: CrossFilter | null) => void;
  setDrillState:     (updater: (prev: Record<string, DrillItem[]>) => Record<string, DrillItem[]>) => void;
  setChartType:      (chartId: string, type: ChartType) => void;
  setSortConfig:     (chartId: string, by: SortBy) => void;
  setTopN:           (chartId: string, n: number | null) => void;
  toggleFeature:     (chartId: string, feature: keyof FeatureFlags) => void;
  setPinnedKPIOrder: (order: string[]) => void;
  addAnnotation:     (chartId: string, ann: Omit<Annotation, 'id' | 'createdAt'>) => void;
  removeAnnotation:  (chartId: string, id: string) => void;
}

const DEFAULT_FEATURES: FeatureFlags = {
  trendLine: false, forecast: false, benchmark: false, brush: false, comparison: false,
};

const DEFAULT_KPI_ORDER = [
  'activeLabs', 'equipmentTypes', 'testCapabilities', 'partnerLabs',
  'openCapas', 'certAlerts', 'batchesOnHold', 'pendingRequests',
];

const LS_PINNED  = 'dash_pinnedKPIOrder';
const LS_TYPES   = 'dash_chartTypes';
const LS_FEATURES = 'dash_features';

function loadLS<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) || '') ?? fallback; }
  catch { return fallback; }
}

// ── Context ───────────────────────────────────────────────────────────────────

const Ctx = createContext<DashboardContextType>({} as DashboardContextType);

export function DashboardProvider({ children }: { children: React.ReactNode }) {
  const [crossFilter,    setCrossFilter]    = useState<CrossFilter | null>(null);
  const [drillState,     setDrillStateRaw]  = useState<Record<string, DrillItem[]>>({});
  const [chartTypes,     setChartTypes]     = useState<Record<string, ChartType>>(() => loadLS(LS_TYPES, {}));
  const [sortConfigs,    setSortConfigs]    = useState<Record<string, SortBy>>({});
  const [topNs,          setTopNs]          = useState<Record<string, number | null>>({});
  const [features,       setFeatures]       = useState<Record<string, FeatureFlags>>(() => loadLS(LS_FEATURES, {}));
  const [pinnedKPIOrder, setPinnedRaw]      = useState<string[]>(() => loadLS(LS_PINNED, DEFAULT_KPI_ORDER));
  const [annotations,    setAnnotations]    = useState<Record<string, Annotation[]>>(() => {
    try {
      const all: Record<string, Annotation[]> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)!;
        if (k.startsWith('ann_')) all[k.slice(4)] = JSON.parse(localStorage.getItem(k)!);
      }
      return all;
    } catch { return {}; }
  });

  // Persist to localStorage
  useEffect(() => { localStorage.setItem(LS_TYPES,    JSON.stringify(chartTypes)); }, [chartTypes]);
  useEffect(() => { localStorage.setItem(LS_FEATURES, JSON.stringify(features));   }, [features]);
  useEffect(() => { localStorage.setItem(LS_PINNED,   JSON.stringify(pinnedKPIOrder)); }, [pinnedKPIOrder]);

  const setDrillState = useCallback(
    (updater: (p: Record<string, DrillItem[]>) => Record<string, DrillItem[]>) =>
      setDrillStateRaw(updater), []
  );

  const setChartType = useCallback((chartId: string, type: ChartType) =>
    setChartTypes((p) => ({ ...p, [chartId]: type })), []);

  const setSortConfig = useCallback((chartId: string, by: SortBy) =>
    setSortConfigs((p) => ({ ...p, [chartId]: by })), []);

  const setTopN = useCallback((chartId: string, n: number | null) =>
    setTopNs((p) => ({ ...p, [chartId]: n })), []);

  const toggleFeature = useCallback((chartId: string, feature: keyof FeatureFlags) => {
    setFeatures((p) => {
      const cur = p[chartId] || { ...DEFAULT_FEATURES };
      const next = { ...cur, [feature]: !cur[feature] };
      return { ...p, [chartId]: next };
    });
  }, []);

  const setPinnedKPIOrder = useCallback((order: string[]) => setPinnedRaw(order), []);

  const addAnnotation = useCallback((chartId: string, ann: Omit<Annotation, 'id' | 'createdAt'>) => {
    setAnnotations((p) => {
      const existing = p[chartId] || [];
      const next = [...existing, { ...ann, id: Date.now().toString(), createdAt: new Date().toISOString() }];
      localStorage.setItem(`ann_${chartId}`, JSON.stringify(next));
      return { ...p, [chartId]: next };
    });
  }, []);

  const removeAnnotation = useCallback((chartId: string, id: string) => {
    setAnnotations((p) => {
      const next = (p[chartId] || []).filter((a) => a.id !== id);
      localStorage.setItem(`ann_${chartId}`, JSON.stringify(next));
      return { ...p, [chartId]: next };
    });
  }, []);

  return (
    <Ctx.Provider value={{
      crossFilter, setCrossFilter,
      drillState, setDrillState,
      chartTypes, setChartType,
      sortConfigs, setSortConfig,
      topNs, setTopN,
      features, toggleFeature,
      pinnedKPIOrder, setPinnedKPIOrder,
      annotations, addAnnotation, removeAnnotation,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useDashboard() {
  return useContext(Ctx);
}
